import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Phone, ShoppingBag } from "lucide-react";
import { scrollToId } from "@/lib/scroll";

const FOOD_GRID =
  "https://customer-assets-cm19k8pv.emergentagent.net/job_7fb684a5-db32-4073-aa6c-6bc4081bd17c/artifacts/zf3qfa5a_image2%20%283%29.jpeg";

const LINES = [
  { text: "GIANT", cls: "text-chalk" },
  { text: "ARTICHOKE", cls: "text-artichoke" },
  { text: "CAFE & GRILL", cls: "font-editorial font-semibold italic text-grape" },
];

const EASE = [0.22, 1, 0.36, 1];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const photoY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const badgeY = useTransform(scrollYProgress, [0, 1], [0, -80]);

  const now = new Date();
  const day = now.getDay();
  const openHour = day === 0 || day === 6 ? 8 : 7;
  const hourNow = now.getHours() + now.getMinutes() / 60;
  const isOpen = hourNow >= openHour && hourNow < 18;

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative overflow-hidden bg-sage pb-20 pt-32 lg:min-h-screen lg:pt-40"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full bg-artichoke/10 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -left-24 bottom-0 h-80 w-80 rounded-full bg-grape/10 blur-3xl"
      />

      <div className="relative mx-auto grid max-w-6xl gap-14 px-5 lg:grid-cols-[1.15fr_0.85fr] lg:items-center">
        <div>
          <motion.p
            data-testid="hero-eyebrow"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1, ease: EASE }}
            className="mb-7 inline-flex items-center rounded-full border border-artichoke/25 bg-cream px-4 py-1.5 text-[10px] font-bold tracking-[0.28em] text-artichoke sm:text-[11px]"
          >
            CASTROVILLE, CA · ARTICHOKE CAPITAL OF THE WORLD
          </motion.p>

          <h1 className="select-none">
            {LINES.map((l, i) => (
              <span key={l.text} className="block overflow-hidden pb-1">
                <motion.span
                  className={`block font-display text-[15vw] font-bold leading-[0.94] tracking-tight sm:text-[12vw] lg:text-[6rem] xl:text-[7rem] ${l.cls}`}
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 1, delay: 0.2 + i * 0.14, ease: EASE }}
                >
                  {l.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.65, ease: EASE }}
            className="mt-6 max-w-md text-base leading-relaxed text-chalk/65"
          >
            A neighborhood deli slinging hand-built sandwiches, small-batch
            coffee, and the crispiest fried artichokes this side of the
            artichoke capital.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.8, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              data-testid="main-hero-order-button"
              href="/order"
              className="group inline-flex items-center gap-3 rounded-full bg-artichoke px-8 py-4 font-display text-lg font-semibold text-cream shadow-xl shadow-artichoke/30 transition-all hover:-translate-y-0.5 hover:bg-deep active:translate-y-0"
            >
              <ShoppingBag className="h-5 w-5 transition-transform group-hover:-rotate-12" />
              Order Pickup Online
            </a>
            <button
              data-testid="hero-menu-button"
              onClick={() => scrollToId("menu")}
              className="inline-flex items-center gap-2 rounded-full border-2 border-chalk/15 bg-cream px-7 py-4 font-display text-lg font-semibold text-chalk transition-all hover:border-grape hover:text-grape"
            >
              See the Menu
              <ArrowDown className="h-4 w-4" />
            </button>
            <a
              data-testid="hero-call-link"
              href="tel:8316334259"
              className="inline-flex items-center gap-2 px-2 py-4 text-sm font-bold text-artichoke underline decoration-dotted underline-offset-4 transition-colors hover:text-grape"
            >
              <Phone className="h-4 w-4" /> (831) 633-4259
            </a>
          </motion.div>

          <motion.div
            data-testid="hero-hours-badge"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.95, ease: EASE }}
            className="mt-6 inline-flex items-center gap-2.5 rounded-full bg-cream px-5 py-2.5 text-sm font-semibold text-chalk/75 shadow-md shadow-artichoke/10"
          >
            <span className="relative flex h-2.5 w-2.5">
              <span
                className={`absolute inline-flex h-full w-full rounded-full opacity-60 ${
                  isOpen ? "animate-ping bg-artichoke" : "bg-crispy"
                }`}
              />
              <span
                className={`relative inline-flex h-2.5 w-2.5 rounded-full ${
                  isOpen ? "bg-artichoke" : "bg-crispy"
                }`}
              />
            </span>
            {isOpen ? "Open now · ’til 6:00 PM" : `Closed · opens ${openHour}:00 AM`}
            <span className="text-chalk/30">·</span>
            <span className="text-chalk/50">Mon–Fri 7–6 · Sat–Sun 8–6</span>
          </motion.div>
        </div>

        <div className="relative">
          <motion.div
            style={{ y: photoY }}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.5, ease: EASE }}
            className="relative overflow-hidden rounded-b-[2rem] rounded-t-[10rem] border-8 border-cream shadow-2xl shadow-artichoke/30"
          >
            <img
              src={FOOD_GRID}
              alt="Sandwiches, burritos and iced coffee at Giant Artichoke Cafe"
              className="h-[420px] w-full object-cover sm:h-[540px]"
              data-testid="hero-photo"
            />
          </motion.div>
          <motion.div
            style={{ y: badgeY }}
            initial={{ opacity: 0, scale: 0.6, rotate: -20 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
            className="absolute -bottom-10 -left-6 sm:-left-10"
          >
            <img
              src="/logo.png"
              alt=""
              data-testid="hero-logo-badge"
              className="spin-slow h-32 w-32 rounded-full shadow-xl ring-4 ring-cream sm:h-40 sm:w-40"
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
